'use client';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect } from 'react';
import {
	BigNum,
	InsuranceFundStake,
	SpotMarketAccount,
	getInsuranceFundStakeAccountPublicKey,
	getInsuranceFundVaultPublicKey,
	PublicKey,
} from '@velocity-exchange/sdk';
import {
	MarketId,
	getMultipleAccountsInfoChunked,
} from '@velocity-exchange/common';
import { fetchInsuranceFundData, IfData } from 'src/utils/insuranceFund';
import useDriftClient from 'src/hooks/useDriftClient';
import useDriftClientIsReady from 'src/hooks/useDriftClientIsReady';
import useWalletIsConnected from 'src/hooks/useWalletIsConnected';
import useGetOraclePriceForMarket from 'src/hooks/useGetOraclePriceForMarket';
import { useSpotMarketsLookup } from 'src/hooks/spotMarkets/useSpotMarketsLookup';
import { createQueryKey } from 'src/utils/query/createQueryKey';
import useCurrentAuthority from 'src/hooks/useCurrentAuthority';
import useDriftAccountStore from 'src/stores/useDriftAccountsStore';
import { AccountLayout } from '@solana/spl-token';
import { useInsuranceFundStatsQuery } from 'src/hooks/query/useInsuranceFundStatsQuery';

export type IfDataLookup = Record<number, IfData>;

const REFETCH_INTERVAL_MS = 30_000;
const STALE_TIME_BUFFER_MS = 5_000;
const IF_DATA_BASE_KEY = createQueryKey('ifData');
const EMPTY_IF_DATA: IfDataLookup = {};

export function useAllIfData() {
	const driftClient = useDriftClient();
	const driftClientIsReady = useDriftClientIsReady();
	const connected = useWalletIsConnected();
	const getOraclePriceForMarket = useGetOraclePriceForMarket();
	const spotMarketsLookup = useSpotMarketsLookup();
	const currentAuthority = useCurrentAuthority();
	const rootAuthority = useDriftAccountStore(
		(s) => s.getCurrentUserAccount()?.authority ?? currentAuthority
	);
	const { data: insuranceFundStats } = useInsuranceFundStatsQuery();

	const queryFn = useCallback(async (): Promise<IfDataLookup> => {
		const spotMarkets = driftClient?.getSpotMarketAccounts();
		const connection = driftClient?.connection;
		const programId = driftClient?.program.programId;

		if (!spotMarkets || !connection || !programId) return {};

		const lookup: IfDataLookup = {};

		// Build list of accounts to fetch
		const vaultPubkeyPromises = spotMarkets.map((spotMarket) =>
			getInsuranceFundVaultPublicKey(programId, spotMarket.marketIndex)
		);
		const vaultPubkeys = await Promise.all(vaultPubkeyPromises);

		const userStakePubkeys: PublicKey[] = [];
		if (connected && rootAuthority) {
			for (const spotMarket of spotMarkets) {
				const userStakePubkey = getInsuranceFundStakeAccountPublicKey(
					programId,
					rootAuthority,
					spotMarket.marketIndex
				);
				userStakePubkeys.push(userStakePubkey);
			}
		}

		// Fetch all accounts in one RPC call
		const allPubkeys = [...vaultPubkeys, ...userStakePubkeys];
		const accountInfos = await getMultipleAccountsInfoChunked(
			connection,
			allPubkeys
		);

		const vaultAccountInfos = accountInfos.slice(0, vaultPubkeys.length);
		const userStakeAccountInfos = accountInfos.slice(vaultPubkeys.length);

		// Process each market
		for (let i = 0; i < spotMarkets.length; i++) {
			const spotMarket: SpotMarketAccount = spotMarkets[i];
			const marketIndex = spotMarket.marketIndex;
			const vaultAccountInfo = vaultAccountInfos[i];

			// Parse vault token balance
			let vaultBalance = BigNum.zero();
			if (vaultAccountInfo?.data) {
				try {
					const tokenAccountData = AccountLayout.decode(vaultAccountInfo.data);
					const balancePrecision = spotMarketsLookup[marketIndex]?.precisionExp;
					if (balancePrecision != null) {
						vaultBalance = BigNum.from(
							tokenAccountData.amount.toString(),
							balancePrecision
						);
					}
				} catch (err) {
					console.warn(
						`[useAllIfData] Failed to parse vault account for market ${marketIndex}:`,
						err
					);
				}
			}

			// Parse user stake account
			let userStakeAccount: InsuranceFundStake | undefined = undefined;
			if (connected && rootAuthority && userStakeAccountInfos[i]?.data) {
				try {
					// Velocity migration: stricter VelocityProgram typing triggers TS2589.
					userStakeAccount = (
						driftClient?.program as any
					)?.account.insuranceFundStake.coder.accounts.decode(
						'InsuranceFundStake',
						userStakeAccountInfos[i]!.data
					);
				} catch (err) {
					console.warn(
						`[useAllIfData] Failed to decode user stake account for market ${marketIndex}:`,
						err
					);
				}
			}

			const oraclePrice =
				getOraclePriceForMarket(
					MarketId.createSpotMarket(marketIndex)
				)?.toNum() || 0;

			// Get 7-day APY from insurance fund stats
			let sevenDayApy = 0;
			if (insuranceFundStats?.marketSharePriceData) {
				const marketData = insuranceFundStats.marketSharePriceData.find(
					(data) => data.marketIndex === marketIndex
				);
				if (marketData?.apy !== undefined) {
					const apy = Number(marketData.apy);
					sevenDayApy = !isNaN(apy) && apy >= 0.01 ? apy : 0;
				}
			}

			const ifData = fetchInsuranceFundData(
				spotMarket,
				oraclePrice,
				vaultBalance,
				userStakeAccount,
				connected,
				sevenDayApy
			);

			lookup[marketIndex] = ifData;
		}

		return lookup;
	}, [
		driftClient,
		connected,
		rootAuthority,
		getOraclePriceForMarket,
		spotMarketsLookup,
		insuranceFundStats,
	]);

	const queryClient = useQueryClient();

	// Refetch when auth state changes or insuranceFundStats loads,
	// instead of changing query key, so existing cached data stays visible during the refetch.
	useEffect(() => {
		queryClient.invalidateQueries({ queryKey: IF_DATA_BASE_KEY });
	}, [connected, rootAuthority, insuranceFundStats, queryClient]);

	return useQuery({
		queryKey: IF_DATA_BASE_KEY,
		queryFn,
		enabled: driftClientIsReady,
		refetchInterval: REFETCH_INTERVAL_MS,
		staleTime: REFETCH_INTERVAL_MS - STALE_TIME_BUFFER_MS,
		initialData: EMPTY_IF_DATA,
	});
}

/**
 * Hook to invalidate IF data without subscribing to it.
 * Use this when you need to trigger a refetch (e.g., after stake/unstake)
 * but don't want to subscribe to the data and cause unnecessary re-renders.
 */
export function useTriggerRefreshIfStakeData() {
	const queryClient = useQueryClient();

	const refresh = useCallback(async () => {
		await queryClient.invalidateQueries({
			queryKey: IF_DATA_BASE_KEY,
		});
	}, [queryClient]);

	return refresh;
}
